import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import Formsy from "formsy-react";
import { Textarea } from "formsy-react-components";
import { Row, Col } from "react-bootstrap";
import { sortBy } from "lodash";

import DatePicker from "../../common/DatePicker";
import DropdownSearch from "../../common/DropdownSearch";
import UserSelect from "../components/UserSelectFormInput";
import PtoRequestDayList from "./PtoRequestDayList";
import { customToast as toast } from "../../../utils";

const PtoRequestForm = ({
  adding,
  editing,
  request,
  codes,
  handleSubmit,
  handleDelete,
  auth,
  history,
  creatingPTO,
  isEditingReserved
}) => {
  const [allowSubmit, setAllowSubmit] = useState(false);
  const [data, setData] = useState({});

  useEffect(() => {
    setData(
      request.employee
        ? { ...request }
        : {
            employee: auth.user._id,
            manager: auth.user.manager,
            department: auth.user.department._id
          }
    );
  }, [request, auth]);

  useEffect(() => {
    const updateDaysArray = () => {
      const days = [];
      const defaultPeriod = codes.find(
        (code) =>
          code.entity === "PTO Request" &&
          code.field === "period" &&
          code.order === 1
      );
      let start = moment(data.start_date);
      const end = moment(data.end_date);
      while (moment(start).isSameOrBefore(moment(end))) {
        if (moment(start).isoWeekday() > 0 && moment(start).isoWeekday() < 6)
          days.push({
            date: start,
            duration: defaultPeriod.order,
            period: defaultPeriod._id
          });
        start = moment(start)
          .startOf("day")
          .add(1, "days")
          .format("YYYY-MM-DD");
      }
      setData((prev) => ({ ...prev, days }));
    };

    if (
      data.start_date &&
      data.end_date &&
      (data.start_date !== request.start_date ||
        data.end_date !== request.end_date)
    ) {
      updateDaysArray();
    }
  }, [
    data.start_date,
    data.end_date,
    codes,
    request.start_date,
    request.end_date
  ]);

  const onSubmit = (form) => {
    if (!data.department) {
      toast.error("No department selected for this PTO.");
    } else {
      // initial status of Pending Inital Approval
      const initialStatus = codes.find(
        (code) =>
          code.entity === "PTO Request" &&
          code.field === "status" &&
          code.order === 1
      );
      const ptoType = codes.find(
        (code) =>
          code.entity === "PTO Request" &&
          code.field === "type" &&
          code.value === "P"
      );
      // total duration of all days
      let totalDuration = 0;
      data.days.forEach((day) => (totalDuration += day.duration));
      const copyData = data;
      delete copyData.typeSelected;
      const newRequest = {
        ...copyData,
        status: isEditingReserved ? copyData.status : initialStatus,
        start_date: data.days[0].date,
        end_date: data.days[data.days.length - 1].date,
        duration: totalDuration,
        request_note: form.request_note,
        type: ptoType._id,
        reservedUpdated: isEditingReserved
      };
      handleSubmit(newRequest);
    }
  };

  const handleChange = (field, value) => {
    if (field === "employee") {
      setData((prev) => ({
        ...prev,
        [field]: value.value,
        manager: value.manager,
        department: value.department._id || value.department
      }));
    } else if (field === "reason") {
      setData((prev) => ({ ...prev, [field]: value.value }));
    } else {
      setData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const onChangeDate = (date, name) => {
    setData((prev) => ({ ...prev, [name]: moment(date).format("YYYY-MM-DD") }));
  };

  const reasonOptions = useMemo(() => {
    const options = codes
      .filter(
        (code) => code.entity === "PTO Request" && code.field === "reason"
      )
      .map((code) => ({
        value: code._id,
        label: code.label,
        order: code.order
      }));

    return sortBy(options, "order");
  }, [codes]);

  return (
    <Formsy
      className="vertical form ptoReqForm"
      onValidSubmit={onSubmit}
      onValid={() => setAllowSubmit(true)}
      onInvalid={() => setAllowSubmit(false)}
    >
      <Row>
        <Col md={12} xs={12}>
          <fieldset>
            <legend>Request Details</legend>
          </fieldset>
        </Col>
      </Row>
      <Row>
        <Col md={3} xs={12}>
          <UserSelect
            value={data.employee}
            name="employee"
            placeholder="Request For"
            disabled={
              !editing || auth.maxRole < 6000 || data?.status?.order === 3
            }
            onChange={handleChange}
            showLabel
          />
        </Col>
        <Col md={3} xs={12}>
          <DropdownSearch
            required
            name="reason"
            disabled={!editing}
            label="Reason"
            onChange={handleChange}
            data={[...reasonOptions]}
            value={data.reason}
          />
        </Col>
        <Col xs={3}>
          <DatePicker
            required={editing}
            name="start_date"
            label="Start Date"
            disabled={!editing}
            onChange={onChangeDate}
            value={data.start_date || null}
            validations={{
              isValidDate(values, value) {
                return (
                  !values.end_date ||
                  moment(value).isSameOrBefore(values.end_date)
                );
              }
            }}
            validationErrors={{
              isValidDate: "Start date should be before end date"
            }}
          />
        </Col>
        <Col xs={3}>
          <DatePicker
            type="date"
            name="end_date"
            label="End Date"
            disabled={!editing}
            onChange={onChangeDate}
            value={data.end_date || null}
            validations={{
              isValidDate(values, value) {
                return !value || moment(value).isSameOrAfter(values.start_date);
              }
            }}
            validationErrors={{
              isValidDate: "End date should be after start date"
            }}
          />
        </Col>
        <Col xs={12}>
          <PtoRequestDayList
            data={data.days}
            onChange={handleChange}
            editing={editing}
            showLabel
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} xs={12}>
          <Textarea
            required={
              data.type &&
              data.typeSelected &&
              data.typeSelected.label !== "PTO" &&
              data.typeSelected.label !== "Sick Time"
            }
            type="text"
            name="request_note"
            label="Note"
            value={data.request_note}
            disabled={!editing}
            validations={{ maxLength: 500 }}
            validationErrors={{
              maxLength: "You can not type in more than 500 characters"
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} className="footer">
          <button
            className="btn btn-cancel"
            onClick={() => history.push("/app/pto")}
            disabled={creatingPTO || !editing}
          >
            Cancel
          </button>
          {!adding &&
            data &&
            data.status &&
            parseInt(data.status.order) === 1 && (
              <button className="btn btn-warning" onClick={handleDelete}>
                Delete
              </button>
            )}
          <input
            className="btn btn-success"
            type="submit"
            disabled={
              creatingPTO ||
              !editing ||
              !allowSubmit ||
              !Array.isArray(data.days) ||
              (Array.isArray(data.days) && data.days.length === 0)
            }
            value={creatingPTO ? "Saving..." : "Save"}
          />
        </Col>
      </Row>
    </Formsy>
  );
};

PtoRequestForm.propTypes = {
  adding: PropTypes.bool.isRequired,
  editing: PropTypes.bool.isRequired,
  request: PropTypes.object,
  codes: PropTypes.array,
  handleSubmit: PropTypes.func,
  handleDelete: PropTypes.func,
  auth: PropTypes.object,
  history: PropTypes.object,
  creatingPTO: PropTypes.bool,
  isEditingReserved: PropTypes.bool
};

PtoRequestForm.defaultProps = {
  request: {},
  creatingPTO: false
};

function mapStatesToProps(state) {
  return {
    auth: state.reducers.auth
  };
}
export default connect(mapStatesToProps)(PtoRequestForm);
